/**
 * VBCropGeometry — where a /video-blur region sits in the exported frame.
 *
 * Why this is a module and not two lines inside the blade
 * ──────────────────────────────────────────────────────
 * The same arithmetic was written TWICE in video-blur.blade.php: once to build
 * the `crop` that cuts the patch out, and once to build the `overlay` that puts
 * the blurred patch back. Those two must agree exactly or the redaction lands
 * somewhere other than the thing it is redacting — the worst outcome this tool
 * has. Two copies of a rule are a rule that can drift.
 *
 * What it fixes
 * ─────────────
 * Neither copy clamped the crop SIZE to the frame. `crop=W:H` with W > frame
 * width is a hard FFmpeg failure, and its log is exactly the pair production
 * has been reporting (reproduced locally with ffmpeg 7.0):
 *
 *     Invalid too big or non positive size for width '500' or height '400'
 *     Failed to configure input pad on Parsed_crop_1
 *     Error reinitializing filters!
 *     Failed to inject frame into filter network: Invalid argument
 *
 * RU, 90 days: 29 sessions on the "reinitializing" line plus 6 more naming an
 * explicit oversized width — counted as two different problems until the local
 * repro showed they are two lines of one failure.
 *
 * Our own arithmetic can produce it: the tracked path pads the max bounding box
 * by 4 px (`round(maxW*vw)+4`), so a region tracked across the full width asks
 * for `vw+4` and fails every time. The static path multiplies a normalised
 * width by the frame and never looks at the ceiling.
 *
 * The clamp is deliberately a clamp and NOT an `min(iw\,W)` expression. Making
 * crop adapt to whatever frame arrives would also swallow a genuine mismatch
 * (rotation misread, mid-stream resolution change) and hand the user an export
 * whose blur is in the wrong place — on a redaction tool, quietly succeeding in
 * the wrong position is worse than failing. Clamping only ever corrects OUR
 * arithmetic against dimensions we already know.
 */
(function (global) {
    'use strict';

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

    /* A region that never moves: normalised box → integer pixel box that is
       guaranteed to fit inside vw×vh. */
    function staticBox(r, vw, vh) {
        var w = Math.max(2, Math.min(vw, Math.round(r.w * vw)));
        var h = Math.max(2, Math.min(vh, Math.round(r.h * vh)));
        var x = clamp(Math.round(r.x * vw), 0, Math.max(0, vw - w));
        var y = clamp(Math.round(r.y * vh), 0, Math.max(0, vh - h));
        return { w: w, h: h, x: x, y: y };
    }

    /* A tracked region: one crop window big enough for every keyframe, with the
       4 px of slack the tracker needs — but never bigger than the frame, which
       is the case the slack used to break. `x`/`y` are the ceilings the animated
       position expressions are clamped into. */
    function trackedBox(keyframes, vw, vh) {
        var maxW = 0, maxH = 0;
        for (var i = 0; i < (keyframes || []).length; i++) {
            var k = keyframes[i];
            if (k.w > maxW) maxW = k.w;
            if (k.h > maxH) maxH = k.h;
        }
        var w = Math.max(4, Math.min(vw, Math.round(maxW * vw) + 4));
        var h = Math.max(4, Math.min(vh, Math.round(maxH * vh) + 4));
        return { w: w, h: h, x: Math.max(0, vw - w), y: Math.max(0, vh - h) };
    }

    global.VBCropGeometry = { staticBox: staticBox, trackedBox: trackedBox };
})(typeof globalThis !== 'undefined' ? globalThis : this);
